import FilterListRoundedIcon from "@mui/icons-material/FilterListRounded";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import { Box, Drawer, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import useAPI from "../../../api/hooks/useAPI";
import useColors from "../../../api/hooks/useColors";
import { bootstrapKeys } from "../../../api/queryKeys";
import ConfirmationDialog from "../../../components/dialogs/ConfirmationDialog";
import { BootstrapContext } from "../../../contexts/BootstrapContext";
import SvgToIcon from "../../../icons/SvgToIcon";
import {
  NotificationFilterTitle,
  NotificationType,
} from "../../../utils/constants";
import { icons, sidebarIcons } from "../../stack/environment/icons/iconExport";
import FilterSubmenu from "./FilterSubmenu";
import InboxNotificationList from "./InboxNotificationList";
import CustomMenu from "./MoreInfoMenu";
import {
  getNotificationTypeDisplayName,
  getToolDisplayName,
  getToolIcon,
} from "./utils/notificationUtils";

const InboxDrawer = ({
  inboxDrawerOpen,
  setOpenInboxDrawer,
  setUnreadNotifications,
}) => {
  const colors = useColors();
  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [anchorElMenu, setAnchorElMenu] = useState(null);
  const [openDeleteAllDialog, setOpenDeleteAllDialog] = useState(false);
  const [isDeleteAllLoading, setIsDeleteAllLoading] = useState(false);
  const openFilterMenu = Boolean(anchorElFilter);
  const openMenu = Boolean(anchorElMenu);
  const [appliedFiltersNotifications, setAppliedFiltersNotifications] =
    useState({
      all: true,
      unread: false,
      snoozed: false,
      sortByDate: false,
    });

  const [selectedNotificationTypes, setSelectedNotificationTypes] = useState(
    []
  );
  const [selectedTools, setSelectedTools] = useState([]);

  const [anchorElFilterTypes, setAnchorElFilterTypes] = useState(null);
  const [anchorElFilterTools, setAnchorElFilterTools] = useState(null);

  const queryClient = useQueryClient();
  const {
    trackEvent,
    markAllInAppNotificationAsRead,
    deleteAllInAppNotifications,
  } = useAPI();

  const { inboxNotifications: rawInboxNotifications, isLoading } =
    useContext(BootstrapContext);

  const inAppNotifications =
    rawInboxNotifications?.filter((notification) => !notification.is_deleted) ||
    [];

  const handleClickFilterMenu = (event) => {
    trackEvent("User clicked on filter menu in inbox");
    setAnchorElFilter(event.currentTarget);
  };

  const unreadNotificationsCount = inAppNotifications.filter(
    (notification) => !notification.is_read
  ).length;

  useEffect(() => {
    setUnreadNotifications(unreadNotificationsCount);
  }, [unreadNotificationsCount, setUnreadNotifications]);

  // Extract unique notification types and labels from existing notifications
  const availableNotificationTypes = Array.from(
    new Set(
      inAppNotifications
        .map((notification) => notification.notification_type)
        .filter((type) => type && type.trim() !== "")
    )
  ).sort();

  const availableTools = Array.from(
    new Set(
      inAppNotifications
        .filter(
          (notification) =>
            notification.notification_type === NotificationType.HIGH_RISK_GAP
        )
        .map((notification) => notification.icon_name)
        .filter((tool) => tool && tool.trim() !== "")
    )
  ).sort();

  const filteredNotifications = (() => {
    let notifications = [...inAppNotifications];

    if (appliedFiltersNotifications.unread) {
      notifications = notifications.filter(
        (notification) => !notification.is_read
      );
    }
    if (appliedFiltersNotifications.snoozed) {
      notifications = notifications.filter(
        (notification) => notification.snoozed
      );
    }

    if (selectedNotificationTypes.length > 0) {
      notifications = notifications.filter((notification) =>
        selectedNotificationTypes.includes(notification.notification_type)
      );
    }

    if (selectedTools.length > 0) {
      notifications = notifications.filter((notification) =>
        selectedTools.includes(notification.icon_name)
      );
    }

    if (appliedFiltersNotifications.sortByDate) {
      notifications = notifications.sort((a, b) => a.timestamp - b.timestamp);
    } else {
      notifications = notifications.sort((a, b) => b.timestamp - a.timestamp);
    }
    return notifications;
  })();

  const handleCloseFilterMenu = () => {
    trackEvent("User closed filter menu");
    setAnchorElFilter(null);
  };

  const handleMultiSelectToggle = (sectionKey, itemValue) => {
    if (sectionKey === NotificationFilterTitle.NOTIFICATION_TYPE) {
      setSelectedNotificationTypes((prev) => {
        const newSelection = prev.includes(itemValue)
          ? prev.filter((type) => type !== itemValue)
          : [...prev, itemValue];

        trackEvent(
          `User ${
            prev.includes(itemValue) ? "unselected" : "selected"
          } notification type: ${itemValue}`
        );
        return newSelection;
      });
    } else if (sectionKey === NotificationFilterTitle.INVOLVED_TOOLS) {
      setSelectedTools((prev) => {
        const newSelection = prev.includes(itemValue)
          ? prev.filter((tool) => tool !== itemValue)
          : [...prev, itemValue];

        trackEvent(
          `User ${
            prev.includes(itemValue) ? "unselected" : "selected"
          } tool: ${itemValue}`
        );
        return newSelection;
      });
    }
  };

  const handleClickMenu = (event) => {
    trackEvent("User clicked on more actions menu in inbox");
    setAnchorElMenu(event.currentTarget);
  };

  const handleDeleteAllNotifications = async () => {
    setIsDeleteAllLoading(true);

    try {
      await deleteAllInAppNotifications();
      trackEvent("Delete all");
      queryClient.invalidateQueries({
        queryKey: bootstrapKeys.all,
      });
      setOpenDeleteAllDialog(false);
    } finally {
      setIsDeleteAllLoading(false);
    }
  };

  const handleCloseMenu = () => {
    trackEvent("User closed more actions menu in inbox");
    setAnchorElMenu(null);
  };

  const sortMenuItems = [
    {
      title: NotificationFilterTitle.ALL,
      key: "all",
      icon: icons.InboxAllNotificationIcon,
      onclick: () => {
        trackEvent("User selected all notifications");
        setAppliedFiltersNotifications({
          all: true,
          unread: false,
          snoozed: false,
          sortByDate: false,
        });
        setSelectedNotificationTypes([]);
        setSelectedTools([]);
        handleCloseFilterMenu();
      },
      disabled: false,
    },
    {
      title: NotificationFilterTitle.UNREAD,
      key: "unread",
      icon: icons.InboxUnreadIcon,
      onclick: () => {
        trackEvent("User selected unread notifications");
        setAppliedFiltersNotifications((prev) => ({
          ...prev,
          all: false,
          unread: !prev.unread,
          snoozed: false,
        }));
      },
      disabled: false,
    },
    {
      title: NotificationFilterTitle.SNOOZED,
      icon: icons.SnoozedIcon,
      key: "snoozed",
      onclick: () => {
        trackEvent("User selected snoozed notifications");
        setAppliedFiltersNotifications((prev) => ({
          ...prev,
          all: false,
          unread: false,
          snoozed: !prev.snoozed,
        }));
      },
      disabled: true,
    },
    {
      title: NotificationFilterTitle.SORT_BY_DATE,
      icon: appliedFiltersNotifications.sortByDate
        ? icons.ArrowDownIcon
        : icons.ArrowUpIcon,
      key: "sortByDate",
      onclick: () => {
        trackEvent("User selected sort by date");
        setAppliedFiltersNotifications((prev) => ({
          ...prev,
          sortByDate: !prev.sortByDate,
        }));
      },
      disabled: false,
    },
    {
      title: `${NotificationFilterTitle.NOTIFICATION_TYPE} ${
        selectedNotificationTypes.length > 0
          ? `(${selectedNotificationTypes.length})`
          : ""
      }`,
      key: "filterByType",
      icon: sidebarIcons.EnvironmentIcon,
      onclick: (event) => {
        setAnchorElFilterTypes(event.currentTarget);
      },
      disabled: availableNotificationTypes.length === 0,
      trailingIcon: icons.chevronRightIcon,
    },
    {
      title: `${NotificationFilterTitle.INVOLVED_TOOLS} ${
        selectedTools.length > 0 ? `(${selectedTools.length})` : ""
      }`,
      key: "filterByTool",
      icon: icons.IntegrationsIcon,
      onclick: (event) => {
        setAnchorElFilterTools(event.currentTarget);
      },
      disabled: availableTools.length === 0,
      trailingIcon: icons.chevronRightIcon,
    },
  ];

  const moreActions = [
    {
      title: "Mark all as read",
      icon: icons.InboxAllNotificationIcon,
      onclick: async () => {
        await markAllInAppNotificationAsRead(
          inAppNotifications.map((notification) => notification.timestamp)
        ).then(() => {
          trackEvent("Mark all as read");
          queryClient.invalidateQueries({
            queryKey: bootstrapKeys.all,
          });
          handleCloseMenu();
        });
      },
      disabled: false,
    },
    {
      title: "Delete all",
      icon: icons.DeleteIcon,
      onclick: async () => {
        trackEvent("User clicked on delete all notifications");
        setOpenDeleteAllDialog(true);
        handleCloseMenu();
      },
      disabled: false,
    },
  ];

  return (
    <Drawer
      anchor="left"
      variant="permanent"
      open={inboxDrawerOpen}
      sx={{
        "& .MuiDrawer-paper": {
          zIndex: "500", // This is the z-index for modal dialog in the app
          width: inboxDrawerOpen ? 380 : 0,
          position: "relative",
          transition: "width 0.3s",
          backgroundColor: colors.grey_50,
          overflowX: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          padding: "16px 4px 24px 0",
          flexGrow: 1,
          width: "380px",
          opacity: inboxDrawerOpen ? 1 : 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: colors.grey_50,
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            overflow: "hidden",
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              paddingLeft: "16px",
            }}
          >
            Inbox
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: "4px",
              alignItems: "center",
              paddingRight: "16px",
            }}
          >
            {/* collapse button */}
            <Box
              onClick={() => {
                trackEvent("User closed inbox drawer");
                setOpenInboxDrawer((prev) => false);
              }}
              sx={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: colors.primary_main,
                },
              }}
            >
              <SvgToIcon
                IconLogo={icons.collapseInbox}
                style={{
                  width: "16px",
                  height: "16px",
                }}
              />
            </Box>
            {/* filter button */}
            <FilterListRoundedIcon
              sx={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: colors.primary_main,
                },
              }}
              onClick={handleClickFilterMenu}
            />
            {/* Main Filter Menu */}
            <CustomMenu
              title="Filter"
              anchorElMenu={anchorElFilter}
              openMenu={openFilterMenu}
              handleCloseMenu={handleCloseFilterMenu}
              moreActions={sortMenuItems}
              appliedFilters={appliedFiltersNotifications}
            />
            {/* Submenu for Notification Types Filter - controlled by "Filter by Type" in sortMenuItems */}
            <FilterSubmenu
              anchorEl={anchorElFilterTypes}
              open={Boolean(anchorElFilterTypes)}
              onClose={() => {
                setAnchorElFilterTypes(null);
              }}
              title="Select notification type..."
              items={availableNotificationTypes}
              selectedItems={selectedNotificationTypes}
              onItemToggle={(item) =>
                handleMultiSelectToggle(
                  NotificationFilterTitle.NOTIFICATION_TYPE,
                  item
                )
              }
              getIcon={(type) =>
                getNotificationTypeDisplayName(type).iconPlaceholder
              }
              getDisplayName={(type) =>
                getNotificationTypeDisplayName(type).displayName
              }
              colors={colors}
            />

            {/* Submenu for Tools Filter - controlled by "Filter by Tool" in sortMenuItems */}
            <FilterSubmenu
              anchorEl={anchorElFilterTools}
              open={Boolean(anchorElFilterTools)}
              onClose={() => {
                setAnchorElFilterTools(null);
              }}
              title="Select involved tools..."
              items={availableTools}
              selectedItems={selectedTools}
              onItemToggle={(item) =>
                handleMultiSelectToggle(
                  NotificationFilterTitle.INVOLVED_TOOLS,
                  item
                )
              }
              getIcon={getToolIcon}
              getDisplayName={getToolDisplayName}
              colors={colors}
            />
            {/* more options button */}
            <MoreHorizRoundedIcon
              onClick={handleClickMenu}
              sx={{
                width: "16px",
                height: "16px",
                cursor: "pointer",
                "&:hover": {
                  color: colors.primary_main,
                },
              }}
            />
            {/* more options menu */}
            <CustomMenu
              anchorElMenu={anchorElMenu}
              openMenu={openMenu}
              handleCloseMenu={handleCloseMenu}
              moreActions={moreActions}
            />
          </Box>
        </Box>
      </Box>
      <InboxNotificationList
        inAppNotifications={filteredNotifications}
        isLoading={isLoading}
      />
      {/* Delete all dialog */}
      <ConfirmationDialog
        openDialog={openDeleteAllDialog}
        dialogTitle={"Delete all notifications"}
        dialogContentFirstLine={
          "Are you sure you want to delete all notifications?"
        }
        handleConfirm={handleDeleteAllNotifications}
        handleDialogClose={() => {
          setOpenDeleteAllDialog(false);
        }}
        isLoading={isDeleteAllLoading}
      />
    </Drawer>
  );
};

export default InboxDrawer;
